/**
 * @fileoverview Main Layout Component
 * @description Primary layout wrapper with Alaska Airlines styled sidebar and header
 * @module components/layout/MainLayout
 */

import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from '../Header';
import { Sidebar } from '../Sidebar';
import { useMediaQuery } from '@/hooks';
import './MainLayout.css';

// Keep the shared header aligned with every page rendered by App.jsx.
const routeTitles = {
  '/': 'Quality Engineering Portal',
  '/github-metrics': 'GitHub Copilot Analytics',
  '/test-calendar': 'Test Calendar',
  '/pnr-generator': 'PNR Generator',
  '/model-context-protocol': 'AI Native Test Automation',
  '/access-request': 'Request Access',
  '/incident-analysis': 'Incident Intelligence',
  '/github-user-metrics': 'GitHub User Metrics',
  '/ai-leverage': 'AI Leveraged Test Generation',
  '/copilot-cost': 'Copilot FinOps',
  '/pipeline-analysis': 'Pipeline Analysis',
  '/pipeline-service': 'Pipeline Service',
  '/project-savings-report': 'Test Automation Leverage Report',
  '/lines-of-code': 'Lines of Code Report',
  '/report-logs': 'Report Logs',
  '/reports': 'Incident Analyser',
  '/regression-optimizer': 'Regression Pack Optimizer',
  '/zamla-report': 'ZAMLA Report',
  '/portfolio/jcte': 'Jeppesen Crew Tracking Editor',
  '/portfolio/airport-ops': 'Airport Operations',
  '/deadhead-pnr': 'DH PNR Generator',
  '/ai-test-data-generation': 'Agentic Test Data Orchestrator',
  '/flight-crew-deadletter': 'Flight Crew DLQ Search',
  '/servicebus-explorer': 'Service Bus Explorer',
  '/flight-crew-compare': 'Flight vs Crew Compare',
  '/crew-pipeline-check': 'Crew Pipeline Check',
  '/admin/rbac': 'Access Management',
};

const getRouteTitle = (pathname) => {
  let normalizedPath = pathname;
  while (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  if (routeTitles[normalizedPath]) {
    return routeTitles[normalizedPath];
  }

  // Future portfolio teams still get meaningful context without another
  // incorrect global fallback.
  if (normalizedPath.startsWith('/portfolio/')) {
    return 'Portfolio Solutions';
  }

  return '';
};

/**
 * Main layout component that wraps page content
 * @returns {JSX.Element} MainLayout component
 */
export const MainLayout = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const currentTitle = getRouteTitle(location.pathname);

  return (
    <div className="main-layout">
      <Sidebar 
        collapsed={!isMobile && sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
      />
      
      <div className={`content-wrapper ${sidebarCollapsed && !isMobile ? 'sidebar-collapsed' : ''}`}>
        <Header 
          onMenuClick={() => setMobileMenuOpen(true)} 
          title={currentTitle}
        />
        
        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
